import type { StanWiersza } from '@/lib/porownanie-wynikow';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  SORTOWANIE WYNIKU PO KLIKNIĘCIU W NAGŁÓWEK — „Działania na bazie" (29.09.2026).
 *
 *  ⚠️⚠️ Z PLANU ETAPU 1 („wyniki z sortowaniem i eksportem"), który zszedł bez sortowania.
 *  Sortujemy u siebie, bez ponownego zapytania: wynik to najwyżej tysiąc wierszy, a przy
 *  porównaniu drugie zapytanie znaczyłoby dwie bazy odpytane jeszcze raz.
 *
 *  ⚠️ KOLEJNOŚĆ TO LISTA INDEKSÓW WIERSZY, A NIE POSORTOWANA KOPIA. Naprawy z porównania i edycja
 *  w siatce trzymają się indeksu wiersza z wyniku — posortowana kopia podpięłaby zmianę pod
 *  inny wiersz niż ten, który człowiek kliknął.
 *
 *  ⚠️ KOLUMNĘ PAMIĘTAMY RAZEM Z NAZWĄ: „Odśwież wynik" zostawia sortowanie, a zapytanie o inne
 *  kolumny gubi je samo, zamiast sortować po czymś, czego już nie ma pod tym numerem.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export type Sortowanie = { kolumna: number; nazwa: string; malejaco: boolean } | { kolumna: 'STAN'; malejaco: boolean };

/** Typy, które porównujemy jak liczby — nazwy typów z deklaracji sterownika MSSQL. */
const LICZBOWE = /^(tinyint|smallint|int|bigint|decimal|numeric|money|smallmoney|float|real|bit)\b/i;

/**
 * PORÓWNANIE DWÓCH WARTOŚCI JAK W BAZIE: `NULL` najniżej, liczby jak liczby, reszta bez wielkości liter.
 *
 * ⚠️ DATY SĄ NAPISAMI `RRRR-MM-DD GG:MM:SS.mmm` (agent pisze je ze składowych), więc porządek
 * napisu jest porządkiem czasu — nie parsujemy ich przez `Date`, które dołożyłoby strefę.
 */
export function porownajWartosci(a: string | null, b: string | null, typ: string): number {
  if (a === b) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  if (LICZBOWE.test(typ)) {
    const x = Number(a);
    const y = Number(b);
    if (Number.isFinite(x) && Number.isFinite(y) && x !== y) return x < y ? -1 : 1;
  }
  return a.localeCompare(b, 'pl', { sensitivity: 'base', numeric: true });
}

/** Stabilna kolejność indeksów — przy równych wartościach zostaje porządek z bazy. */
export function kolejnosc(ile: number, porownaj: ((a: number, b: number) => number) | null, malejaco = false): number[] {
  const indeksy = Array.from({ length: ile }, (_, i) => i);
  if (porownaj === null) return indeksy;
  const znak = malejaco ? -1 : 1;
  return indeksy.sort((a, b) => znak * porownaj(a, b) || a - b);
}

/** Czy zapamiętane sortowanie po kolumnie nadal pasuje do tego wyniku. */
export function kolumnaSortowania(sort: Sortowanie | null, kolumny: readonly { nazwa: string }[]): number | null {
  if (sort === null || sort.kolumna === 'STAN') return null;
  return kolumny[sort.kolumna]?.nazwa === sort.nazwa ? sort.kolumna : null;
}

/** Kolejność wierszy wyniku po kolumnie — dla siatki, edycji i eksportu. */
export function kolejnoscWyniku(
  wiersze: readonly (readonly (string | null)[])[],
  kolumny: readonly { nazwa: string; typ: string }[],
  sort: Sortowanie | null,
): number[] {
  const c = kolumnaSortowania(sort, kolumny);
  if (c === null) return kolejnosc(wiersze.length, null);
  const typ = kolumny[c]!.typ;
  return kolejnosc(wiersze.length, (a, b) => porownajWartosci(wiersze[a]![c] ?? null, wiersze[b]![c] ?? null, typ), sort!.malejaco);
}

/** Przy sortowaniu po stanie porównania rozjazdy idą pierwsze — po to się tę kolumnę klika. */
export const RANGA_STANU: Record<StanWiersza, number> = { INNY: 0, TYLKO_TU: 1, BRAK_TU: 2, ZGODNY: 3 };

/** Kolejne kliknięcie w ten sam nagłówek: rosnąco → malejąco → bez sortowania. */
export function nastepneSortowanie(obecne: Sortowanie | null, klik: { kolumna: number; nazwa: string } | 'STAN'): Sortowanie | null {
  const ten = klik === 'STAN' ? obecne?.kolumna === 'STAN' : obecne !== null && obecne.kolumna === klik.kolumna && 'nazwa' in obecne && obecne.nazwa === klik.nazwa;
  if (!ten) return klik === 'STAN' ? { kolumna: 'STAN', malejaco: false } : { ...klik, malejaco: false };
  if (!obecne!.malejaco) return { ...obecne!, malejaco: true };
  return null;
}
